<template>
    <div class="flex flex-col dark:text-white">
        <!-- Estudo exploratório: quantas contas já desviam hoje -->
        <div class="mb-6">
            <h2 class="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Desvio entre janelas — hoje</h2>
            <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div class="rounded-lg bg-slate-50 p-4 dark:bg-slate-800/25">
                    <div class="mb-2 flex items-center justify-between">
                        <font-awesome-icon :icon="['fas', 'users']" class="h-5 w-5 text-indigo-600" />
                        <span class="text-xs text-gray-500 dark:text-gray-400">Base 90d</span>
                    </div>
                    <p class="text-2xl font-bold text-gray-900 dark:text-white">{{ formatNumber(summary.accounts_with_volume) }}</p>
                    <p class="text-xs text-gray-500 dark:text-gray-400">Contas com vendas de cartão na janela mais longa</p>
                </div>
                <div class="rounded-lg bg-red-50 p-4 dark:bg-red-900/20">
                    <div class="mb-2 flex items-center justify-between">
                        <font-awesome-icon :icon="['fas', 'triangle-exclamation']" class="h-5 w-5 text-red-600" />
                        <span class="text-xs text-gray-500 dark:text-gray-400">Desvio hoje</span>
                    </div>
                    <p class="text-2xl font-bold text-red-700 dark:text-red-400">{{ formatNumber(summary.accounts_with_deviation) }}</p>
                    <p class="text-xs text-gray-500 dark:text-gray-400">Janela curta muito pior que a longa</p>
                </div>
                <div class="rounded-lg bg-slate-50 p-4 dark:bg-slate-800/25">
                    <div class="mb-2 flex items-center justify-between">
                        <font-awesome-icon :icon="['fas', 'percentage']" class="h-5 w-5 text-blue-600" />
                        <span class="text-xs text-gray-500 dark:text-gray-400">Proporção</span>
                    </div>
                    <p class="text-2xl font-bold text-gray-900 dark:text-white">{{ summary.accounts_with_deviation_pct }}%</p>
                    <p class="text-xs text-gray-500 dark:text-gray-400">Das contas com base estatística</p>
                </div>
            </div>
        </div>

        <!-- Busca -->
        <div class="mb-4 flex items-center justify-between rounded-lg bg-slate-50 p-4 dark:bg-slate-800/25">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white">Contas</h2>
            <div class="flex items-center gap-2">
                <input
                    v-model="search"
                    @keyup.enter="fetchSellers(1)"
                    type="text"
                    placeholder="Buscar por e-mail ou ID"
                    class="rounded-lg border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-slate-700 dark:text-white"
                />
                <button
                    @click="fetchSellers(1)"
                    :disabled="loading"
                    class="inline-flex items-center justify-center rounded-md bg-indigo-500 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow transition duration-150 ease-in-out hover:bg-indigo-400"
                >
                    <font-awesome-icon :icon="['fas', 'magnifying-glass']" class="mr-2" />
                    Buscar
                </button>
            </div>
        </div>

        <!-- Tabela de contas -->
        <table-component
            :loading="loading"
            :pagination="pagination"
            :headers="headers"
            @fetch="fetchSellers"
        >
            <template v-for="seller in sellers" :key="seller.producer_id">
                <tr class="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40" @click="toggleDetail(seller)">
                    <td class="table-cell-sm">
                        <div class="font-medium text-gray-900 dark:text-white">{{ seller.email }}</div>
                        <div class="text-xs text-gray-500 dark:text-gray-400">ID: {{ seller.producer_id }}</div>
                    </td>
                    <td class="table-cell-sm">
                        <span v-if="seller.score !== null" class="text-sm text-gray-700 dark:text-gray-300">{{ seller.score }}</span>
                        <span v-else class="text-xs text-gray-400">-</span>
                    </td>
                    <td class="table-cell-sm text-right">
                        <span class="font-mono text-sm" :class="rateClass(seller.windows[7].chargeback_rate, 2.5, 1)">
                            {{ formatPercent(seller.windows[7].chargeback_rate) }}
                        </span>
                    </td>
                    <td class="table-cell-sm text-right">
                        <span class="font-mono text-sm" :class="rateClass(seller.windows[90].chargeback_rate, 2.5, 1)">
                            {{ formatPercent(seller.windows[90].chargeback_rate) }}
                        </span>
                    </td>
                    <td class="table-cell-sm">
                        <span
                            v-if="seller.deviation.is_deviating"
                            class="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-800 dark:bg-red-900 dark:text-red-200"
                        >
                            Desvio {{ seller.deviation.short_window }}d vs {{ seller.deviation.long_window }}d
                        </span>
                        <span v-else class="text-xs text-gray-400">estável</span>
                    </td>
                    <td class="table-cell-sm text-right">
                        <span class="font-mono text-sm text-gray-700 dark:text-gray-300">{{ formatPercent(seller.windows[7].approval_rate) }}</span>
                    </td>
                    <td class="table-cell-sm text-right">
                        <span class="font-mono text-sm text-gray-700 dark:text-gray-300">{{ formatCurrency(seller.windows[7].avg_ticket) }}</span>
                    </td>
                </tr>
                <tr v-if="expandedId === seller.producer_id">
                    <td colspan="7" class="bg-slate-50 px-6 py-4 dark:bg-slate-800/25">
                        <div class="overflow-x-auto">
                            <table class="min-w-full text-sm">
                                <thead>
                                    <tr class="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
                                        <th class="pb-2 pr-4">Janela</th>
                                        <th class="pb-2 pr-4 text-right">Chargeback</th>
                                        <th class="pb-2 pr-4 text-right">Reembolso</th>
                                        <th class="pb-2 pr-4 text-right">MED</th>
                                        <th class="pb-2 pr-4 text-right">Aprovação</th>
                                        <th class="pb-2 pr-4 text-right">Ticket médio</th>
                                        <th class="pb-2 text-right">Base (cartão)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="w in windowKeys" :key="w" class="border-t border-gray-200 dark:border-gray-700">
                                        <td class="py-1.5 pr-4 font-medium text-gray-700 dark:text-gray-300">{{ w }}d</td>
                                        <td class="py-1.5 pr-4 text-right font-mono" :class="rateClass(seller.windows[w].chargeback_rate, 2.5, 1)">
                                            {{ formatPercent(seller.windows[w].chargeback_rate) }}
                                        </td>
                                        <td class="py-1.5 pr-4 text-right font-mono">{{ formatPercent(seller.windows[w].refund_rate) }}</td>
                                        <td class="py-1.5 pr-4 text-right font-mono text-gray-400" title="MED (Mecanismo Especial de Devolução): sem fonte de dados identificada ainda">
                                            n/d
                                        </td>
                                        <td class="py-1.5 pr-4 text-right font-mono">{{ formatPercent(seller.windows[w].approval_rate) }}</td>
                                        <td class="py-1.5 pr-4 text-right font-mono">{{ formatCurrency(seller.windows[w].avg_ticket) }}</td>
                                        <td class="py-1.5 text-right font-mono text-gray-500 dark:text-gray-400">{{ seller.windows[w].card_total_count }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </td>
                </tr>
            </template>
        </table-component>

        <p class="mt-3 text-xs text-gray-500 dark:text-gray-400">
            A disputa conta pelo dia em que o <strong>evento chegou</strong>, não pelo dia da venda, sobre as vendas de
            cartão/carteira digital da janela — é a mesma régua do risco de chargeback do cockpit de saques. Contar pela data
            da venda deixaria a janela curta sempre vazia (a disputa chega semanas depois) e nenhum desvio apareceria.
            Pix e boleto não entram: não têm chargeback.
            MED (disputa Pix) ainda não tem fonte de dados mapeada — coluna exibida como indisponível até definição.
        </p>
    </div>
</template>

<script>
import TableComponent from "../../components/layouts/table/TableComponent.vue";
import axios from 'axios';
import { useToast } from "vue-toastification";

export default {
    components: {
        TableComponent,
    },
    setup() {
        const toast = useToast();
        return { toast };
    },
    data() {
        return {
            loading: false,
            search: '',
            expandedId: null,
            windowKeys: [7, 15, 30, 60, 90],
            summary: {
                accounts_with_volume: 0,
                accounts_with_deviation: 0,
                accounts_with_deviation_pct: 0,
            },
            sellers: [],
            pagination: {
                total: 0,
                per_page: 20,
                current_page: 1,
                last_page: 1,
                from: 1,
                to: 20,
            },
        }
    },
    computed: {
        headers() {
            return [
                "Vendedor",
                "Score",
                "Chargeback 7d",
                "Chargeback 90d",
                "Desvio",
                "Aprovação 7d",
                "Ticket médio 7d",
            ];
        }
    },
    mounted() {
        this.fetchSummary();
        this.fetchSellers();
    },
    methods: {
        fetchSummary() {
            axios.get('/api/account-window-metrics/summary')
                .then(response => {
                    this.summary = response.data.data;
                })
                .catch(error => {
                    console.error('Erro ao carregar resumo de desvio:', error);
                });
        },
        fetchSellers(page = 1) {
            this.loading = true;
            this.pagination.current_page = page;

            axios.get('/api/account-window-metrics/sellers', {
                params: {
                    page,
                    per_page: this.pagination.per_page,
                    search: this.search || undefined,
                },
            })
                .then(response => {
                    const data = response.data.data;
                    this.sellers = data.data;
                    this.pagination = {
                        ...this.pagination,
                        total: data.total,
                        last_page: data.last_page,
                    };
                })
                .catch(error => {
                    console.error('Erro ao carregar contas:', error);
                    this.toast.error("Erro ao carregar métricas por janela");
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        toggleDetail(seller) {
            this.expandedId = this.expandedId === seller.producer_id ? null : seller.producer_id;
        },
        formatPercent(value) {
            if (value === null || value === undefined) {
                return "n/d";
            }
            return `${Number(value).toFixed(2)}%`;
        },
        formatCurrency(cents) {
            if (cents === null || cents === undefined) {
                return "n/d";
            }
            return new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            }).format(Number(cents) / 100);
        },
        formatNumber(value) {
            return new Intl.NumberFormat('pt-BR').format(value || 0);
        },
        rateClass(value, dangerAt, warnAt) {
            if (value === null || value === undefined) {
                return "text-gray-400";
            }
            if (value >= dangerAt) {
                return "text-red-600 dark:text-red-400";
            }
            if (value >= warnAt) {
                return "text-yellow-600 dark:text-yellow-400";
            }
            return "text-gray-900 dark:text-white";
        },
    }
}
</script>
